import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import 'antd/dist/reset.css'; // Ant Design v5+ uses 'reset.css'
import { BrowserRouter as Router, Route, Routes, Link, Navigate, useLocation } from "react-router-dom";
import { Layout, Menu, ConfigProvider, theme, Drawer, Button, Tag, Tooltip } from "antd";
import Cookies from "js-cookie";
import { io } from "socket.io-client";
import { useQuery } from "@tanstack/react-query";
import { Vortex } from "react-loader-spinner";
import { debounce } from "lodash";
import {
  SyncOutlined,
  OrderedListOutlined,
  BoxPlotOutlined,
  EditOutlined,
  HomeOutlined,
  SettingOutlined,
  CarFilled,
  TeamOutlined,
  CheckSquareOutlined,
  StockOutlined,
  CloudSyncOutlined,
  WechatWorkOutlined,
  MenuOutlined,
  ImportOutlined,
  FileTextOutlined,
  RetweetOutlined,
  PieChartOutlined,
  SwapOutlined,
  QuestionCircleOutlined,
  DropboxOutlined ,
  SnippetsFilled,
  ThunderboltFilled,
  TruckFilled,
  TabletFilled,
  ProductFilled,
  InteractionFilled,
  AlertOutlined,
  
} from "@ant-design/icons";

import config from "./config.js";
import DeliveryPage from "./components/DeliveryPage";
import UserPerformancePage from "./components/UserPerformancePage.js";
import SyncPage from "./components/SyncPage";
import OrdersPage from "./components/OrdersPage";
import PackingScreen from "./components/Packingscreen";
import PickingPage from "./components/PickingPage.js";
import UpdateProductIds from "./components/UpdateProductIDs.js";
import DashboardPage from "./components/DashboardPage.js";
import CustomHeader from "./components/Header";
import SignIn from "./components/SignIn";
import SignUp from "./components/SignUp";
import SettingsPage from "./components/SettingsPage";
import StockAssessmentPage from "./components/StockAssessmentPage";
import OdooSync from "./components/OdooSync.js";
import InstallPrompt from "./components/InstallPrompt.js";
import RTOInboundPage from "./components/RTOInboundPage";
import RTOReportingPage from "./components/RTOReportingPage";
import RTOInventoryPage from "./components/RTOInventoryPage";
import ReplacementSheetPage from "./components/ReplacementSheetPage"; // Import Replacement Sheet
import EscalationsClaimsPage from "./components/EscalationsClaimsPage";
import EscalationDetailPage from "./components/EscalationDetailPage";
import EscalationReportsPage from "./components/EscalationReportsPage";
import PackingMaterialPage from "./components/PackingMaterialPage";
import MasterPackingSheetPage from "./components/MasterPackingSheetPage";
import HelpPage from "./components/HelpPage";
import { ShopOutlined, AppstoreOutlined } from "@ant-design/icons";

import { AuthProvider, useAuth } from "./Context/AuthContext.js";
import { ThemeProvider, useTheme } from "./Context/ThemeContext.js";

const { Content, Footer, Sider } = Layout;

function AppContent() {
  const { 
    token, 
    setToken, 
    userName, 
    setUserName, 
    permissions, 
    setPermissions, 
    loading, 
    handleLogout 
  } = useAuth();
  
  const { isMobile, darkTheme, toggleTheme, sidebarCollapsed, setSidebarCollapsed, mobileDrawerVisible, setMobileDrawerVisible, toggleSidebar } = useTheme();
  const location = useLocation();

  const [columnWidths, setColumnWidths] = useState({});
  const [refreshKey, setRefreshKey] = useState(0);
  const [isSocketConnected, setIsSocketConnected] = useState(false);
  const [packingLiveEvent, setPackingLiveEvent] = useState(null);
  const [lastFetchTime, setLastFetchTime] = useState(new Date().toISOString());
  const activeSharedOrderRoute = ['/orders', '/sync', '/OdooSync'].some(
    (path) => location.pathname === path || location.pathname.startsWith(`${path}/`)
  );
  const liveDashboardUpdatesEnabled = permissions.dashboardPackingLiveUpdates === true && location.pathname === '/';
  const defaultRoute = permissions.dashboardAccess
    ? '/'
    : permissions.syncAccess
      ? '/sync'
      : permissions.ordersAccess
        ? '/orders'
        : permissions.pickingAccess
          ? '/picking'
          : permissions.packingAccess
            ? '/packing'
            : permissions.deliveryAccess
              ? '/delivery'
              : permissions.productsAccess
                ? '/update-product-ids'
                : permissions.stockAssessmentAccess
                  ? '/stock-assessment'
                  : permissions.settingsAccess
                    ? '/settings'
                    : permissions.rtoInboundAccess
                      ? '/rto-inbound'
                      : permissions.rtoReportingAccess
                        ? '/rto-reporting'
                        : permissions.rtoInventoryAccess
                          ? '/rto-inventory'
                          : permissions.replacementsAccess
                            ? '/replacement-sheet'
                            : permissions.escalationsAccess
                              ? '/escalations-claims'
                              : '/help';
  const canLoadSharedOrders = activeSharedOrderRoute && Boolean(
    permissions.ordersAccess 
   
  );

  // Apply theme to document globally (moved from Header.js)
  useEffect(() => {
    document.documentElement.setAttribute('data-theme', darkTheme ? 'dark' : 'light');
    document.body.style.background = darkTheme ? '#141414' : '#ffffff';
  }, [darkTheme]);

  // Ensure sidebar is collapsed on reload so hover logic works correctly
  useEffect(() => {
    if (!isMobile) {
      // Delay slightly to ensure sidebar starts collapsed on reload
      const timer = setTimeout(() => {
        setSidebarCollapsed(true);
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [isMobile, setSidebarCollapsed]);

  // Close mobile drawer when menu item is clicked
  const handleMenuClick = () => {
    if (isMobile) {
      setMobileDrawerVisible(false);
    }
  };

  const fetchOrders = async () => {
    if (!token) return [];
    
    try {
      const response = await axios.get(
        `${config.apiBaseUrl}/orders/all-orders?lastUpdated=${lastFetchTime}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setLastFetchTime(new Date().toISOString());
      return response.data.orders;
    } catch (error) {
      console.error("Error fetching orders:", error);
      throw error;
    }
  };

  const { data: orders, refetch, isFetching } = useQuery({
    queryKey: ["orders"],
    queryFn: fetchOrders,
    enabled: !!token && canLoadSharedOrders, // Only fetch when authenticated and a permitted page needs shared orders
    refetchInterval: isSocketConnected ? false : 3600000, // 60 min if WebSocket down
    refetchOnWindowFocus: false,
    staleTime: Infinity,
    onSuccess: (data) => {
      console.log("Fetched orders:", data);
    },
    onError: (error) => {
      console.error("Error fetching orders:", error);
    },
  });

  const debouncedRefetch = useMemo(() => debounce(() => {
    if (canLoadSharedOrders) refetch();
  }, 5000), [canLoadSharedOrders, refetch]); // Wait 5 seconds between refetches

  useEffect(() => {
    // Do not open a live-update socket unless the explicit permission is enabled.
    // This avoids unnecessary connections and prevents disabled users from
    // processing packing events in the dashboard.
    if (!token || !liveDashboardUpdatesEnabled) {
      setIsSocketConnected(false);
      setPackingLiveEvent(null);
      return;
    }

    const socket = io(config.apiBaseUrl.replace('/api', ''), { reconnection: false });
    socket.on("connect", () => {
      setIsSocketConnected(true);
      socket.emit('join-dashboard-packing-live', { token });
      console.log("Connected to WebSocket server");
    });
    socket.on("disconnect", () => {
      setIsSocketConnected(false);
      console.log("Disconnected from WebSocket server");
    });
    socket.on("order-updated", () => {
      console.log("Order updated via WebSocket");
      debouncedRefetch();
    });
    socket.on('packing-completed', (event) => {
      setPackingLiveEvent({ ...event, receivedAt: new Date().toISOString() });
      debouncedRefetch();
    });
    return () => {
      socket.disconnect();
      debouncedRefetch.cancel();
    };
  }, [debouncedRefetch, token, liveDashboardUpdatesEnabled]);

  const handleSyncComplete = () => {
    debouncedRefetch();
  };

  const handleResize = (index) => (e, { size }) => {
    setColumnWidths((prev) => ({
      ...prev,
      [columns[index].key]: size.width,
    }));
  };

  const columns = [
    // Define your table columns here
  ];

  const enhancedColumns = columns.map((col, index) => ({
    ...col,
    onHeaderCell: (column) => ({
      width: column.width,
      onResize: handleResize(index),
    }),
  }));

  const onLogoutClick = () => {
    handleLogout();
    if (isMobile) {
      setMobileDrawerVisible(false);
    }
  };

  // Menu component to avoid duplication
  const buildMenuItems = () => {
    const items = [];

    if (permissions.dashboardAccess) {
      items.push({
        key: 'dashboard',
        icon: <HomeOutlined  style={{fontSize:'24px'}}/>,
        label: <Link to="/">dashboard</Link>,        
      });
    }

    if (permissions.syncAccess) {
      items.push({
        key: '12',
        icon: <CloudSyncOutlined />,
        label: <Link to="/OdooSync">Orders Sync</Link>,
      });
    }

    if (permissions.ordersAccess) {
      items.push({
        key: '3',
        icon: <ThunderboltFilled />,
        label: <Link to="/orders">Orders</Link>,
        onClick: () => {
          setRefreshKey((prevKey) => prevKey + 1);
          handleMenuClick();
        },
      });
    }

    if (permissions.pickingAccess) {
      items.push({
        key: '10',
        icon: <TabletFilled />,
        label: <Link to="/picking">Picking</Link>,
      });
    }
 if (permissions.packingAccess) {
      items.push({
        key: '4',
        icon: <BoxPlotOutlined />,
        label: <Link to="/packing">Packing</Link>,
      });   
    
    }
    if (permissions.deliveryAccess) {
      items.push({
        key: '7',
        icon: <TruckFilled />,
        label: <Link to="/delivery">Delivery</Link>,
      });
    }

    if (permissions.productsAccess) {
      items.push({
        key: '5',
        icon: <ProductFilled />,
        label: <Link to="/update-product-ids">Products</Link>,
      });
    }

    if (permissions.stockAssessmentAccess) {
      items.push({
        key: '11',
        icon: <StockOutlined />,
        label: <Link to="/stock-assessment">Stock Assessment</Link>,
      });
    }
if (permissions.stockAssessmentAccess) {
  items.push({
        key: 'packing-material',
        icon: <DropboxOutlined />,
        label: <Link to="/packing-material">Packing Material</Link>,
      });
      items.push({
        key: 'master-packing-sheet',
        icon: <SnippetsFilled />,
        label: <Link to="/master-packing-sheet">Master Packing Sheet</Link>,
      });
}
    if (permissions.rtoInboundAccess) {
      items.push({
        key: '13',
        icon: <ImportOutlined />,
        label: <Link to="/rto-inbound">RTO Inbound</Link>,
      });
    }

    if (permissions.rtoReportingAccess) {
      items.push({
        key: '14',
        icon: <PieChartOutlined />,
        label: <Link to="/rto-reporting">RTO Reporting</Link>,
      });
    }

    if (permissions.rtoInventoryAccess) {
      items.push({
        key: '15',
        icon: <AppstoreOutlined />,
        label: <Link to="/rto-inventory">RTO Inventory</Link>,
      });
    }   
    if (permissions.replacementsAccess) {
      items.push({
        key: '17',
        icon: <InteractionFilled />,
        label: <Link to="/replacement-sheet">Replacements </Link>,
      });
    }
    if (permissions.escalationsAccess) {
      items.push({
        key: 'escalations-claims',
        icon: <AlertOutlined />,
        label: <Link to="/escalations-claims">Escalations &amp; Claims</Link>,
      });
    }

    if (permissions.settingsAccess) {
      items.push({
        key: '6',
        icon: <SettingOutlined />,
        label: <Link to="/settings">Settings</Link>,
      });
    }

    items.push({
      key: 'help',
      icon: <QuestionCircleOutlined />,
      label: <Link to="/help">Help &amp; shortcuts</Link>,
    });

    return items;
  };

  const renderMenu = () => (
    <Menu
      theme={darkTheme ? "dark" : "light"}
      defaultSelectedKeys={["1"]}
      selectedKeys={
        typeof window !== "undefined" && window.location.pathname.startsWith("/escalations-claims")
          ? ["escalations-claims"]
          : undefined
      }
      mode="inline"
      style={{
        backgroundColor: darkTheme ? '#07130F' : '#fff',
        borderRight: 'none',
        fontWeight: 500,
      }}
      onClick={handleMenuClick}
      items={buildMenuItems()}
    />
  );

  // Show loading spinner while initializing
  if (loading) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
        }}
      >
        <Vortex
          visible={true}
          height="80"
          width="80"
          ariaLabel="vortex-loading"
          colors={["red", "green", "blue", "yellow", "orange", "purple"]}
        />
      </div>
    );
  }

  return (
    <ConfigProvider
      theme={{
        algorithm: darkTheme ? theme.darkAlgorithm : theme.defaultAlgorithm,
        token: {
          colorPrimary: '#0B5D3B',
          borderRadius: 12,
        },
        components: {
          Menu: {
            itemBorderRadius: 12,
            itemMarginInline: 12,
            itemHeight: 45,
            iconSize: 22,
            itemColor: darkTheme ? '#B7C2BD' : '#17211D',
            itemHoverColor: darkTheme ? '#F5F7F6' : '#08734A',
            itemHoverBg: darkTheme ? 'rgba(50, 184, 120, 0.10)' : '#F3F7F5',
            itemSelectedColor: darkTheme ? '#55D995' : '#0B5D3B',
            itemSelectedBg: darkTheme ? 'rgba(50, 184, 120, 0.16)' : '#EAF7F0',
            itemActiveBg: darkTheme ? 'rgba(50, 184, 120, 0.12)' : '#F3F7F5',
          },
          Layout: {
            siderBg: darkTheme ? '#07130F' : '#ffffff',
          },
        },
      }}
    >
      <style>
        {`
          .ant-menu-item .anticon,
          .ant-menu-submenu-title .anticon {
            transition: all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
          }
          .ant-menu-item:hover .anticon,
          .ant-menu-submenu-title:hover .anticon {
            transform: scale(1.25);
            color: #08734A;
          }
          .ant-menu-item-selected .anticon,
          .ant-menu-submenu-selected .anticon {
            transform: scale(1.15);
            color: #0B5D3B;
          }
          .ant-menu-inline-collapsed .ant-menu-item .anticon,
          .ant-menu-inline-collapsed .ant-menu-submenu-title .anticon {
            font-size: 24px;
          }
          .ant-menu-inline-collapsed .ant-menu-item:hover .anticon,
          .ant-menu-inline-collapsed .ant-menu-submenu-title:hover .anticon {
            transform: scale(1.35);
            filter: drop-shadow(0 0 5px rgba(50, 184, 120, 0.35));
          }
          .dark-theme .ant-menu-item-selected .anticon,
          .dark-theme .ant-menu-submenu-selected .anticon {
            color: #55D995;
          }
          aside.ant-layout-sider {
            transition: all 0.4s cubic-bezier(0.23, 1, 0.32, 1) !important;
          }
        `}
      </style>
      <Layout style={{ minHeight: "100vh" }} className={darkTheme ? 'dark-theme' : 'light-theme'}>
          {token ? (
            <>
              {/* Desktop Sidebar */}
              {!isMobile && (
                <Sider 
                  collapsible 
                  collapsed={sidebarCollapsed}
                  onCollapse={setSidebarCollapsed}
                  theme={darkTheme ? "dark" : "light"} 
                  width={230}
                  trigger={null}
                  onMouseEnter={() => setSidebarCollapsed(false)}
                  onMouseLeave={() => setSidebarCollapsed(true)}
                  style={{ 
                    backgroundColor: darkTheme ? '#07130F' : '#fff',
                    boxShadow: darkTheme ? '2px 0 8px rgba(0,0,0,0.5)' : '2px 0 8px rgba(0,0,0,0.05)',
                    zIndex: 1001,
                    height: '100vh',
                    position: 'fixed',
                    left: 0,
                    top: 0,
                  }}
                  collapsedWidth={70}
                >
                  <div className="logo" style={{ padding: "16px 8px", textAlign: "center", display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <img
                      src={sidebarCollapsed ? "/TGH%20Short%20Logo.webp" : "/TGH%20New%20LOG0.webp"}
                      alt="Logo"
                      style={{
                        width: sidebarCollapsed ? "28px" : "150px",
                        height: sidebarCollapsed ? "28px" : "38px",
                        maxWidth: "100%",
                        objectFit: "contain",
                        transition: "all 0.3s ease",
                        filter: darkTheme ? 'none' : 'brightness(0)',
                      }}
                      className="app-logo"
                    />
                  </div>
                  {renderMenu()}
                </Sider>
              )}

              {/* Mobile Drawer */}
              {isMobile && (
                <Drawer
                  title={
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                      <img
                        src="/TGH%20Short%20Logo.webp"
                        alt="Logo"
                        style={{
                          width: "28px",
                          height: "28px",
                          marginRight: "8px",
                          objectFit: "contain",
                          filter: darkTheme ? 'none' : 'brightness(0)'
                        }}
                      />
                      Menu
                    </div>
                  }
                  placement="left"
                  onClose={() => setMobileDrawerVisible(false)}
                  open={mobileDrawerVisible}
                  styles={{
                    body: {
                      padding: 0,
                      backgroundColor: darkTheme ? '#07130F' : '#fff'
                    }
                  }}
                  headerStyle={{
                    backgroundColor: darkTheme ? '#07130F' : '#fff',
                    borderBottom: darkTheme ? '1px solid #303030' : '1px solid #f0f0f0'
                  }}
                  width={280}
                >
                  {renderMenu()}
                </Drawer>
              )}

              <Layout
                className="site-layout"
                style={{
                  marginLeft: isMobile ? 0 : sidebarCollapsed ? 70 : 230,
                  transition: 'margin-left 0.3s ease',
                  minHeight: '100vh'
                }}
              >
                {/* Header status bar only reads existing app state – nothing else changed */}
                <CustomHeader
                  userName={userName}
                  setToken={setToken}
                  setUserName={setUserName}
                  handleLogout={onLogoutClick}
                  darkTheme={darkTheme}
                  toggleTheme={toggleTheme}
                  onMenuClick={toggleSidebar}
                  orders={orders}
                  isMobile={isMobile}
                  sidebarCollapsed={sidebarCollapsed}
                  lastSyncTime={lastFetchTime}
                  onRefresh={refetch}
                  refreshing={isFetching}
                  isSocketConnected={isSocketConnected}
                />
                <Content style={{ 
                  background: darkTheme ? '#141414' : '#f5f5f5',
                  transition: 'background 0.3s ease',
                  padding: isMobile ? '12px 12px 0' : '1px',
                  overflowX: 'hidden',
                  overflowY: 'auto'
                }}>
                  
                  
                  <Routes>
                    {permissions.dashboardAccess && (
                      <>
                        <Route path="/" element={<DashboardPage
                          orders={orders}
                          darkTheme={darkTheme}
                          isMobile={isMobile}
                          packingLiveUpdate={packingLiveEvent}
                          livePackingUpdatesEnabled={liveDashboardUpdatesEnabled}
                        />} />
                        <Route path="/user-performance" element={<UserPerformancePage darkTheme={darkTheme} isMobile={isMobile}  />} />
                      </>
                    )}
                    {permissions.syncAccess && (
                      <Route path="/sync" element={<SyncPage onSyncComplete={handleSyncComplete} darkTheme={darkTheme} isMobile={isMobile} orders={orders} />} />
                    )}
                    {permissions.ordersAccess && (
                      <Route
                        path="/orders"
                        key={refreshKey}
                        element={
                          <OrdersPage
                            orders={orders || []}
                            columnWidths={columnWidths}
                            handleResize={handleResize}
                            enhancedColumns={enhancedColumns}
                            darkTheme={darkTheme}
                            isMobile={isMobile}
                            onRefresh={refetch}
                            isRefreshing={isFetching}
                          />
                        }
                      />
                    )}
                    {permissions.pickingAccess && (
                      <Route
                        path="/picking"
                        element={<PickingPage orders={orders} userName={userName} refetch={refetch} darkTheme={darkTheme} isMobile={isMobile} />}
                      />
                    )}
                    {permissions.packingAccess && (
                      <Route path="/packing" element={<PackingScreen userName={userName} darkTheme={darkTheme} isMobile={isMobile} />} />
                    )}
                    {permissions.packingAccess && (
                      <Route path="/packing-material/*" element={<PackingMaterialPage darkTheme={darkTheme} isMobile={isMobile} />} />
                    )}
                    {permissions.packingAccess && (
                      <Route path="/master-packing-sheet" element={<MasterPackingSheetPage darkTheme={darkTheme} isMobile={isMobile} />} />
                    )}
                    {permissions.deliveryAccess && (
                      <Route
                        path="/delivery"
                        element={<DeliveryPage orders={orders} refetch={refetch} darkTheme={darkTheme} isMobile={isMobile} />}
                      />
                    )}
                    {permissions.productsAccess && (
                      <Route path="/update-product-ids" element={<UpdateProductIds darkTheme={darkTheme} isMobile={isMobile} />} />
                    )}
                    {permissions.settingsAccess && (
<Route path="/settings" element={<SettingsPage token={token} userName={userName} darkTheme={darkTheme} toggleTheme={toggleTheme} isMobile={isMobile} />} />
                    )}
                    {permissions.settingsAccess && (
                      <Route path="/OdooSync" element={<OdooSync darkTheme={darkTheme} userName={userName} isMobile={isMobile} />} />
                    )}
                    {permissions.stockAssessmentAccess && (
                      <Route path="/stock-assessment" element={<StockAssessmentPage darkTheme={darkTheme} isMobile={isMobile} />} />
                    )}
                    {permissions.ordersAccess && (
                      <Route path="/rto-inbound" element={<RTOInboundPage darkTheme={darkTheme} isMobile={isMobile} />} />
                    )}
                    {permissions.ordersAccess && (
                      <Route path="/rto-reporting" element={<RTOReportingPage darkTheme={darkTheme} isMobile={isMobile} />} />
                    )}
                    {permissions.ordersAccess && (
                      <Route path="/rto-inventory" element={<RTOInventoryPage darkTheme={darkTheme} isMobile={isMobile} />} />
                    )}                  
                    {permissions.ordersAccess && (
                      <Route path="/replacement-sheet" element={<ReplacementSheetPage darkTheme={darkTheme} isMobile={isMobile} />} />
                    )}
                    {permissions.escalationsAccess && (
                      <Route path="/escalations-claims" element={<EscalationsClaimsPage darkTheme={darkTheme} isMobile={isMobile} />} />
                    )}
                    {permissions.escalationsAccess && (
                      <Route path="/escalations-claims/new" element={<EscalationsClaimsPage darkTheme={darkTheme} isMobile={isMobile} autoOpenNew />} />
                    )}
                    {permissions.escalationsAccess && (
                      <Route path="/escalations-claims/reports" element={<EscalationReportsPage darkTheme={darkTheme} isMobile={isMobile} />} />
                    )}
                    {permissions.escalationsAccess && (
                      <Route path="/escalations-claims/:id" element={<EscalationDetailPage darkTheme={darkTheme} isMobile={isMobile} />} />
                    )}
                    <Route path="/help" element={<HelpPage darkTheme={darkTheme} isMobile={isMobile} />} />
                    <Route path="*" element={<Navigate to={defaultRoute} replace />} />
                  </Routes>
                </Content>
            <Footer
  style={{
    background: darkTheme ? '#0d1512' : '#f6faf7',
    borderTop: darkTheme ? '1px solid #21312a' : '1px solid #e4ece7',
    padding: isMobile ? '16px 14px' : '20px 40px',
    marginTop: 'auto',
  }}
>
  <div
    style={{
      maxWidth: 1480,
      margin: '0 auto',
      display: 'flex',
      flexDirection: isMobile ? 'column' : 'row',
      alignItems: isMobile ? 'center' : 'center',
      justifyContent: 'space-between',
      gap: isMobile ? 12 : 24,
    }}
  >
    {/* Left – Brand + Copyright */}
    <div style={{ textAlign: isMobile ? 'center' : 'left' }}>
      <div
        style={{
          fontSize: 15,
          fontWeight: 700,
          color: darkTheme ? '#edf4f0' : '#11221b',
          letterSpacing: '-0.01em',
        }}
      >
        The Good Health
      </div>
      <div
        style={{
          fontSize: 12.5,
          color: darkTheme ? '#7f9088' : '#79897f',
          marginTop: 2,
        }}
      >
        © {new Date().getFullYear()} · TGH Packing Console · All rights reserved. 
      </div>
    </div>

    {/* Center – Status / Tagline (optional) */}
    {!isMobile && (
      <div
        style={{
          fontSize: 13,
          color: darkTheme ? '#7f9088' : '#79897f',
          display: 'flex',
          alignItems: 'center',
          gap: 8,
        }}
      >
          
      </div>
    )}

    {/* Right – Install + optional help */}
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        flexWrap: 'wrap',
        justifyContent: isMobile ? 'center' : 'flex-end',
      }}
    >
      <InstallPrompt />
      {/* Uncomment if you want Help & Support back */}
      {/* <Helpandsupport userName={userName} darkTheme={darkTheme} /> */}
    </div>
  </div>
</Footer>
              </Layout>
            </>
          ) : (
            <Routes>
              <Route
                path="/signin"
                element={
                  <SignIn 
                    setToken={setToken} 
                    setUserName={setUserName} 
                    setPermissions={setPermissions} 
                    darkTheme={darkTheme}
                    isMobile={isMobile}
                  />
                }
              />
              <Route path="/signup" element={<SignUp darkTheme={darkTheme} isMobile={isMobile} />} />
              <Route path="*" element={<Navigate to="/signin" />} />
            </Routes>
          )}
        </Layout>
    </ConfigProvider>
  );
}

function App() {
  return (
    <AuthProvider>
      <ThemeProvider>
        <Router>
          <AppContent />
        </Router>
      </ThemeProvider>
    </AuthProvider>
  );
}

export default App;
